<template>
  <div>
    <b-section-loader :loading="$loading.isLoading('packageSchemaPersonShort')">
      <div class="mb-3">Спосіб генерації ID:</div>
      <package-id-mode-select
        v-model="idMode"
        class="mb-3"
        auto-description="Система присвоїть унікальний ID для пов'язаної особи"></package-id-mode-select>
      <b-input
        v-if="idMode === 'manual'"
        v-model="manualId"
        placeholder="Введіть ID пов'язаної особи"
        hide-details
        :error-messages="getErrorMessages(v$.manualId)"
        @blur="v$.manualId.$touch()"></b-input>
      <v-alert
        v-if="existingPerson"
        class="mt-3"
        :type="canUseExistingPerson ? 'info' : 'error'"
        variant="tonal"
        density="compact">
        Особа з таким ID вже є у вкладці "{{
          isExistingPersonRelated
            ? "Пов'язані особи з боржником"
            : "Інші пов'язані особи"
        }}": {{ getPackagePersonName(existingPerson) }}.
        {{
          canUseExistingPerson
            ? 'Новий запис не буде створено - буде використано наявний.'
            : 'Вкажіть інший ID.'
        }}
      </v-alert>
      <template v-if="!canUseExistingPerson">
        <div class="my-3">
          <span v-if="dialog.dialogData.subtitle">{{
            dialog.dialogData.subtitle
          }}</span>
          <span>Оберіть тип особи, щоб заповнити дані:</span>
        </div>
        <package-directory-select
          v-model="personSchemaCredentials.personType"
          :height-full="false"
          :directory="directory.F082"></package-directory-select>
      </template>
    </b-section-loader>
  </div>
</template>

<script lang="ts">
import { computed, PropType, reactive, ref } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { getErrorMessages } from 'best-modules/utils'
import PackageDirectorySelect from './PackageDirectorySelect.vue'
import PackageIdModeSelect from './PackageIdModeSelect.vue'
import { PersonShortSchemaCredentials } from '@/utils/types'
import { usePackageDirectory } from '@/store/package-directory'
import { getPackageSchemaPersonShort } from '@/request/report'
import {
  findPersonShort,
  getPackagePersonName,
} from '@/pages/report/package/package-table/helpers'
import { v4 as uuid } from 'uuid'
import { Dialog } from 'best-modules/plugins'
import { BSectionLoader } from 'best-modules/components'

export default {
  name: 'PackagePersonShort',
  components: { PackageDirectorySelect, BSectionLoader, PackageIdModeSelect },
  props: {
    dialog: {
      type: Object as PropType<Dialog<'PackagePersonShort'>>,
    },
  },
  methods: { getErrorMessages, getPackagePersonName },
  setup(props) {
    const { directory, fillDirectory } = usePackageDirectory()
    fillDirectory()
    const personSchemaCredentials = reactive<PersonShortSchemaCredentials>({
      personType: '1',
    })

    const idMode = ref<'auto' | 'manual'>('auto')
    const manualId = ref('')

    // особа, ID якої вже є в person_short пакета
    const existingPerson = computed(() => {
      if (idMode.value !== 'manual' || !manualId.value) return null
      return (
        findPersonShort(props.dialog.dialogData.personShort, manualId.value) ||
        null
      )
    })
    const isExistingPersonRelated = computed<boolean>(() => {
      return !!props.dialog.dialogData.relatedPersonIds?.includes(
        manualId.value
      )
    })
    // замість нового person_short використовується наявний запис
    const canUseExistingPerson = computed<boolean>(() => {
      if (!existingPerson.value) return false
      switch (props.dialog.dialogData.target) {
        case 'relatedPerson':
          return !isExistingPersonRelated.value
        case 'collateralOwner':
          return true
        default:
          return false
      }
    })

    const submit = async () => {
      await v$.value.$validate()
      if (v$.value.$invalid) return

      return getPackageSchemaPersonShort(personSchemaCredentials).then(res => {
        const person_id_short =
          idMode.value === 'manual' ? manualId.value : uuid()
        res.person_short[0].person_id_short = person_id_short
        res.person_short[0].f082_person_type =
          personSchemaCredentials.personType
        res.related_person[0].person_info.person_id_short = person_id_short

        return res
      })
    }

    const rules = {
      personType: { required },
      manualId: {
        required: (value: string) =>
          idMode.value === 'manual' ? !!value : true,
        personShortUnique: () =>
          !existingPerson.value || canUseExistingPerson.value,
      },
    }

    const v$ = useVuelidate(rules, { ...personSchemaCredentials, manualId })

    return {
      personSchemaCredentials,
      idMode,
      manualId,
      existingPerson,
      isExistingPersonRelated,
      canUseExistingPerson,
      v$,
      submit,
      directory,
    }
  },
}
</script>

<style scoped lang="scss"></style>
